import { post } from 'stores/_services/dataService';
import { IToast } from '../../contexts/toastContext';

export interface LoginResponse {
    route: string | { pathname: string; state: any };
    token?: string;
    ttl?: string;
    ssoLogoutUrl?: string;
    loginMessage?: IToast;
}

export default class Auth {
    private accessToken: string | null;
    private idToken: string | null;
    private expiresAt: number | null;

    constructor() {
        this.accessToken = '';
        this.idToken = '';
        this.expiresAt = null;
    }

    async handleAuthentication(origin: string): Promise<LoginResponse> {
        let route;
        let response;
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorMessage = urlParams.get('error_description');

        try {
            response = await post('users/login', { code, state, origin });

            if (response?.data?.error) {
                throw response.data.error;
            }
        } catch (error: any) {
            const message = error.message;

            if (message === 'k1LoginRequired') {
                route = {
                    pathname: '/login',
                    state: { error: 'This user is required to use KaseyaOne login' }
                };
            } else if (message === 'k1MasterRoleRequired') {
                const redirectUri = window.sessionStorage.getItem('redirectUri');
                window.sessionStorage.removeItem('redirectUri');
                return {
                    route: redirectUri ? redirectUri : '/dashboard',
                    loginMessage: {
                        title: 'KaseyaOne Master role required',
                        message: 'This operation requires user to have the KaseyaOne Master role assigned.',
                        type: 'error'
                    }
                };
            } else if (message === 'k1GroupMembershipRequired') {
                route = {
                    pathname: '/login',
                    state: { error: 'User is not a member of any of allowed KaseyaOne groups' }
                };
            } else if (message === 'otpExpired' || message === 'userIsNonMember') {
                const tempRoute = message === 'otpExpired' ? '/reset' : '/unauthorized';

                if (errorMessage && errorMessage.indexOf('::authuser::') > -1) {
                    const errorDetail = errorMessage.replace('::authuser::', '') + ' is not authorized.';
                    route = tempRoute + '?message=' + errorDetail;
                } else {
                    route = tempRoute + '?message=' + 'User is not authorized.';
                }
            } else if (message === 'mfaRequired') {
                route = '/mfaRequired';
            } else {
                route = '/error';
            }

            return { route };
        }

        let redirectUri;

        if (response.data.user.needsPasswordReset) {
            redirectUri = '/change-password';
        } else {
            redirectUri = window.sessionStorage.getItem('redirectUri');
            window.sessionStorage.removeItem('redirectUri');
        }

        if (redirectUri) {
            route = redirectUri;
        } else if (response.data.redirect) {
            route = response.data.redirect;
        } else {
            route = '/dashboard';
        }

        const result: LoginResponse = {
            route,
            token: response.data.id,
            ttl: response.data.ttl,
            ssoLogoutUrl: response.data.ssoLogoutUrl
        };

        if (response.data.loginMessage) {
            result.loginMessage = {
                title: response.data.loginMessage.title,
                message: response.data.loginMessage.message,
                type: response.data.loginMessage.type || 'success'
            };
        }

        return result;
    }

    getAccessToken() {
        return this.accessToken;
    }

    getIdToken() {
        return this.idToken;
    }

    setSession(authResult: { accessToken: string; idToken: string; expiresIn: number }) {
        // Set the time that the access token will expire at
        this.accessToken = authResult.accessToken;
        this.idToken = authResult.idToken;
        this.expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
    }

    isAuthenticated() {
        // Check whether the current time is past the access token's expiry time
        const expiresAt = this.expiresAt;
        return new Date().getTime() < (expiresAt as number);
    }
}
